import type { JSX, ReactNode } from 'react';
import { createContext, use, useState } from 'react';
import { useNavigationHash } from 'ts/base/hooks/UseNavigationHash';
import { useSidebarToggle } from 'ts/base/perspective/sidebar/SidebarToggleHook';
import { Accordion, AccordionContent, AccordionTitle } from 'ts/components/Accordion';
import { Icon, type IconName } from 'ts/components/Icon';
import { Menu, MenuItem } from 'ts/components/Menu';
import './RightSidebar.less';

/**
 * Implements the outer shell of the right sidebar. The content typically consists of one or multiple SidebarSection
 * components.
 */
export function RightSidebar({
	accordion = true,
	children
}: {
	accordion?: boolean;
	children: ReactNode;
}): JSX.Element {
	const hash = useNavigationHash();
	const [isCollapsed, toggleManually] = useSidebarToggle(hash.getViewId());
	return (
		<SidebarToggleStateContext value={{ isCollapsed, toggleManually }}>
			<aside id="right-sidebar-wrapper" className={isCollapsed ? 'small-right-sidebar' : ''}>
				<div
					id="right-sidebar-container"
					className={'ui visible vertical flat right overlay sidebar ' + (isCollapsed ? 'very thin' : '')}
				>
					{accordion ? (
						<Accordion fluid styled id="right-sidebar-accordion" exclusive={false}>
							{children}
						</Accordion>
					) : (
						<div id="right-sidebar">{children}</div>
					)}
				</div>
				<Menu
					id="right-sidebar-toggle-menu"
					onClick={toggleManually}
					className={'sidebar-toggle-menu right ' + (isCollapsed ? 'minimized' : '')}
				>
					<MenuItem link id="toggle-right-sidebar-button">
						<Icon name={isCollapsed ? 'angle double left' : 'angle double right'} />
						<span className="grey item__text">Collapse</span>
					</MenuItem>
				</Menu>
			</aside>
		</SidebarToggleStateContext>
	);
}

const SidebarToggleStateContext = createContext<{ isCollapsed: boolean; toggleManually: () => void } | undefined>(
	undefined
);

/** Contains the base props of the typically used sidebar sections. */
export const SIDEBAR_SECTIONS = {
	TEST_COVERAGE: {
		icon: 'check',
		title: 'Test Coverage'
	},
	QUALITY_GOAL_SETTINGS: {
		icon: 'flag checkered',
		title: 'Quality Goal Settings'
	},
	FILTER: {
		icon: 'filter',
		title: 'Filter Options'
	},
	LOG_FILTER: {
		icon: 'filter',
		title: 'Log Filter Options'
	},
	FINDINGS: {
		icon: 'exclamation triangle',
		title: 'Findings'
	},
	SUMMARY: {
		icon: 'info',
		title: 'Summary'
	},
	PARSE_ISSUES: {
		icon: 'exclamation triangle',
		title: 'Parsing issues found!'
	},
	METRICS: {
		icon: 'percent',
		title: 'Metrics'
	},
	CODE_REVIEW: {
		icon: 'comments',
		title: 'Code Review Status'
	},
	SLIDE_SETTINGS: {
		icon: 'cog',
		title: 'Slide Settings'
	},
	DISPLAY_OPTIONS: {
		icon: 'cog',
		title: 'Display Options'
	}
} as const;

/** Props for SidebarSection. */
type SidebarSectionProps = {
	children: ReactNode;
	icon: IconName;
	title: string;
	collapsable?: boolean;
};

/** Represents a collapsable subsection in the right sidebar. */
export function SidebarSection({ children, icon, title, collapsable = true }: SidebarSectionProps) {
	const [active, setActive] = useState(true);
	const { isCollapsed, toggleManually } = use(SidebarToggleStateContext)!;
	const onTitleClick = () => {
		if (isCollapsed) {
			toggleManually();
			setActive(true);
		} else if (collapsable) {
			setActive(!active);
		}
	};
	return (
		<>
			<AccordionTitle active={active} onClick={onTitleClick}>
				<Icon name={icon} />
				<span className="item__text">{title}</span>
			</AccordionTitle>
			<AccordionContent data-testid={`section-${title}`} active={active}>
				{children}
			</AccordionContent>
		</>
	);
}
